/**
 * Client for the API-key half of the `workspace` edge function. No component
 * talks to `workspace_api_keys` directly. A key is stored only as its hash, so
 * a listed row carries no secret and any member may read the list (the agent
 * web-widget picker reads the public ones); creating and revoking one is for an
 * owner or admin. The whole key is in the create's answer and nowhere else.
 * Field names are the column names. Keep in sync with
 * `supabase/functions/workspace/` (`api-key.*`).
 */

import { api } from "@/lib/api-client";

const BASE = "/workspace";

/** A private key is for server-side code; a public one (`vbr_pub_…`) may sit in a web page. */
export type ApiKeyType = "private" | "public";

/** A key as listed: its first characters, never the key itself. */
export interface ApiKey {
  id: string;
  name: string;
  key_type: ApiKeyType;
  key_prefix: string;
  is_active: boolean | null;
  created_at: string | null;
  last_used_at: string | null;
  expires_at: string | null;
}

/** A key just created: the row, and the whole key — shown once, never stored. */
export interface CreatedApiKey extends ApiKey {
  api_key: string;
}

export interface NewApiKey {
  /** Trimmed by the server; 1–100 characters. */
  name: string;
  /** Private when left out. */
  key_type?: ApiKeyType;
  /** A future date and time; left out (or null), the key never expires. */
  expires_at?: string | null;
}

/** Every key, newest first. */
export const listApiKeys = (workspaceId: string) => api.getList<ApiKey>(`${BASE}/${workspaceId}/api-keys`);

export const createApiKey = (workspaceId: string, key: NewApiKey) =>
  api.post<CreatedApiKey>(`${BASE}/${workspaceId}/api-keys`, key);

/** Deleting the row is what revokes the key. */
export const revokeApiKey = (workspaceId: string, keyId: string) =>
  api.del(`${BASE}/${workspaceId}/api-keys/${keyId}`);
